import React from 'react';
import { Link } from 'react-router-dom';
import { Award, BookOpen, Users, Globe } from 'lucide-react';
import './About.css';
import { professorData, aboutContent, recognitions } from '../utils/mockData';

const About = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <div className="about-hero-content">
            <h1>About Professor Tamvada</h1>
            <p className="about-hero-subtitle">
              A strategic thinker, systems-level problem solver, and trusted advisor to governments, 
              enterprises, and institutions worldwide
            </p>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="about-overview section">
        <div className="container">
          <div className="about-grid">
            <div className="about-text">
              <h2>Strategic Leadership Across Continents</h2>
              <p className="lead-text">{aboutContent.overview}</p>
              <p>
                My journey has taken me from the Max Planck Institute of Economics in Germany, 
                where I earned the prestigious Otto-Hahn Medal, to visiting fellowships at Harvard 
                Business School and Kellogg School of Management in the United States. Today, as 
                Professor of Entrepreneurship at Kingston University London and Director of the RISE 
                Research Excellence Hub, I continue to bridge the worlds of rigorous academic inquiry 
                and practical strategic impact.
              </p>
              <p>
                What distinguishes my approach is the integration of three decades of empirical research 
                with hands-on advisory experience. I don't merely theorize about strategic challenges—I've 
                lived them, studied them, and developed frameworks that have been tested across diverse 
                economic and institutional contexts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="philosophy-section section-light">
        <div className="container">
          <div className="section-header">
            <h2>Advisory Philosophy</h2>
            <p className="section-subtitle">
              Four principles that guide every strategic engagement
            </p>
          </div>
          <div className="philosophy-grid">
            {aboutContent.philosophy.map((item, index) => (
              <div key={index} className="philosophy-card">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="experience-section section">
        <div className="container">
          <div className="section-header">
            <h2>Professional Journey</h2>
            <p className="section-subtitle">
              Leadership positions at world-class institutions
            </p>
          </div>
          <div className="experience-timeline">
            {aboutContent.experience.map((exp, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <div className="timeline-period">{exp.period}</div>
                  <h3 className="timeline-role">{exp.role}</h3>
                  <div className="timeline-institution">{exp.institution}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="credentials-section section-light">
        <div className="container">
          <div className="section-header">
            <h2>Credentials & Qualifications</h2>
          </div>
          <div className="credentials-list">
            {aboutContent.credentials.map((credential, index) => (
              <div key={index} className="credential-item">
                <Award size={20} className="credential-icon" />
                <span>{credential}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recognition Section */}
      <section className="recognition-section section-dark">
        <div className="container">
          <div className="section-header">
            <h2>Awards & Recognition</h2>
            <p className="section-subtitle">
              Global recognition for excellence in research and impact
            </p>
          </div>
          <div className="recognition-grid">
            {recognitions.map((recognition) => (
              <div key={recognition.id} className="recognition-card">
                <div className="recognition-header">
                  <div className="recognition-award">{recognition.award}</div>
                  <div className="recognition-year">{recognition.year}</div>
                </div>
                <div className="recognition-org">{recognition.organization}</div>
                <p className="recognition-description">{recognition.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Areas Section */}
      <section className="impact-section section">
        <div className="container">
          <div className="section-header">
            <h2>Areas of Impact</h2>
            <p className="section-subtitle">
              Deep expertise across multiple domains
            </p>
          </div>
          <div className="impact-grid">
            <div className="impact-card">
              <div className="impact-icon">
                <Globe size={32} />
              </div>
              <h3>Economic Development</h3>
              <p>
                Policy design, entrepreneurship ecosystems, and institutional innovation for 
                governments and development organizations worldwide.
              </p>
            </div>
            <div className="impact-card">
              <div className="impact-icon">
                <BookOpen size={32} />
              </div>
              <h3>Strategic Innovation</h3>
              <p>
                Technology adoption, innovation management, and sustainable transformation for 
                enterprises navigating digital and circular economy transitions.
              </p>
            </div>
            <div className="impact-card">
              <div className="impact-icon">
                <Users size={32} />
              </div>
              <h3>Organizational Leadership</h3>
              <p>
                Executive development, strategic decision-making, and institutional capacity 
                building for leaders at critical transition points.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Beyond Academia Section */}
      <section className="beyond-section section-light">
        <div className="container">
          <h2>Beyond Academia</h2>
          <div className="beyond-content">
            <p className="lead-text">
              While my academic foundation provides intellectual rigor, my impact extends far 
              beyond the ivory tower.
            </p>
            <div className="beyond-grid">
              <div className="beyond-item">
                <h3>Social Entrepreneurship</h3>
                <p>
                  Co-founder of <strong>iPowerz</strong>, a digital social enterprise platform 
                  accelerating microentrepreneurship and small business growth. This initiative 
                  earned recognition as a Steward of the Council for Inclusive Capitalism with 
                  the Vatican.
                </p>
              </div>
              <div className="beyond-item">
                <h3>Advisory Boards</h3>
                <p>
                  Active advisory board member for organizations spanning nonprofit sustainability 
                  initiatives, technology startups, and SME growth ventures across UK, USA, and India.
                </p>
              </div>
              <div className="beyond-item">
                <h3>Nonprofit Leadership</h3>
                <p>
                  Founder and trustee of charitable organizations including Share-a-Meal, India 
                  Knowledge Consortium, and Sri Sathya Sai Charitable Trust UK, focused on social 
                  impact and community development.
                </p>
              </div>
              <div className="beyond-item">
                <h3>Public Discourse</h3>
                <p>
                  Regular contributor to <em>The Conversation</em>, translating complex economic 
                  and business research into accessible insights for public policy discussions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta-section section">
        <div className="container">
          <div className="about-cta-content">
            <h2>Let's Discuss Your Strategic Challenge</h2>
            <p>
              Whether you're navigating organizational transformation, evaluating policy interventions, 
              or seeking strategic clarity at an inflection point, I bring both intellectual depth 
              and practical wisdom to the conversation.
            </p>
            <Link to="/contact" className="btn btn-primary btn-large">
              Book Strategy Session
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;